import { dateToIsoDateString, getClosestPreviousWorkDay } from "@/utils/utils";
import { Button, Calendar, Card, Form, Modal, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ActionButton from "../../../../../common/components/buttons/ActionButton";
import { ModalSizes } from "../../../../../common/constants";
import { OrderDirection } from "../../../../../common/enums";
import { formatLocaleDate } from "../../../../../common/utils/formatUtils";
import { ContractOrderByColumn, ContractView } from "../../../enums";
import { ContractFilterPageRequest } from "../../../types";
import { INSURANCE_CONTRACT_CANCELLING_TERM_DAYS } from "../../../utils";
import { ContractFilterForm } from "../../forms/ContractFilterForm";

type Props = {
  validatedSearchParams?: ContractFilterPageRequest;
  updateSearchParams: (searchParams: ContractFilterPageRequest, append?: boolean, replace?: boolean) => void;
};

export const ContractAnniversaryDateListFilterView = ({ validatedSearchParams, updateSearchParams }: Props) => {
  const { t } = useTranslation();

  const [isCalcFixationAnniversaryModalOpen, setIsCalcFixationAnniversaryModalOpen] = useState(false);
  const [calcFixationAnniversaryDate, setCalcFixationAnniversaryDate] = useState<string>();

  const [form] = Form.useForm<ContractFilterPageRequest>();

  const handleFilterClear = () => {
    form.resetFields();
    updateSearchParams(form.getFieldsValue(), false, true);
  };

  return (
    <>
      <Card
        className="card-box"
        title={<h2>{t("contract.titles.list")}</h2>}
        extra={
          <>
            <>
              <ActionButton
                icon="calculator"
                color="green"
                variant="outlined"
                size="middle"
                onClick={() => setIsCalcFixationAnniversaryModalOpen(true)}
              >
                {t("contract.actions.calculateFixationAnniversaryDate")}
              </ActionButton>

              <Modal
                width={ModalSizes.TINY}
                open={isCalcFixationAnniversaryModalOpen}
                title={t("contract.actions.calculateFixationAnniversaryDate")}
                onCancel={() => setIsCalcFixationAnniversaryModalOpen(false)}
                afterClose={() => setCalcFixationAnniversaryDate(undefined)}
                footer={[
                  <Button key="close" type="primary" onClick={() => setIsCalcFixationAnniversaryModalOpen(false)}>
                    {t("common.close")}
                  </Button>,
                ]}
                mask={{ closable: false }}
              >
                <Calendar
                  fullscreen={false}
                  value={dayjs(calcFixationAnniversaryDate)}
                  onChange={(value) => setCalcFixationAnniversaryDate(dateToIsoDateString(value))}
                />

                <div style={{ marginTop: 16, textAlign: "center" }}>
                  <div>
                    {calcFixationAnniversaryDate ? (
                      <div style={{ fontWeight: 500, fontSize: 24 }}>
                        {formatLocaleDate(
                          getClosestPreviousWorkDay(
                            dayjs(calcFixationAnniversaryDate).subtract(INSURANCE_CONTRACT_CANCELLING_TERM_DAYS, "day"),
                          ),
                        )}
                      </div>
                    ) : (
                      <Typography.Text type="warning" style={{ fontSize: 20 }}>
                        {t("contract.helpers.chooseFixationAnniversaryDate")}
                      </Typography.Text>
                    )}
                  </div>
                  <div>
                    <Typography.Text type="secondary" italic>
                      {t("contract.helpers.contractTerminationLastDate")}
                    </Typography.Text>
                  </div>
                </div>
              </Modal>
            </>

            <ActionButton color="danger" size="middle" variant="outlined" icon="reload" onClick={handleFilterClear}>
              {t("common.resetFilter")}
            </ActionButton>
          </>
        }
      >
        <ContractFilterForm
          form={form}
          report={ContractView.INTERNAL_ANNIVERSARY_DATE_REPORT}
          orderBy={[ContractOrderByColumn.LAST_CONTRACT_CANCELLATION_DATE]}
          orderDirections={[OrderDirection.ASC]}
          updateSearchParams={updateSearchParams}
          validatedSearchParams={validatedSearchParams}
        />
      </Card>
    </>
  );
};
